import React, { useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQueryGetExamPaper } from "@/apis/exam_papers/getExamPaper";
import ModuleNavigationModal, {
  type ModuleInfo,
} from "@/components/exam/shared/ModuleNavigationModal";
import { Button } from "@/components/button";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import PageContainer from "@/components/layouts/PageContainer";
import { Text } from "@/components/text";
import { Heading } from "@/components/heading";
import type { IQuestion } from "@/apis/exam_papers/types";

const TeacherDemoPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  // Get exam paper data
  const {
    data: examPaper,
    isLoading: isPaperLoading,
    error: paperError,
  } = useQueryGetExamPaper(id || "");

  // UI States
  const [moduleNavOpen, setModuleNavOpen] = useState(false);
  const [currentModuleId, setCurrentModuleId] = useState("1");
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);

  // Local state (not saved to server)
  const [localHighlights, setLocalHighlights] = useState<{
    [key: string]: string;
  }>({});
  const [localAnswers, setLocalAnswers] = useState<{ [key: string]: any }>({});
  const [showAnswers, setShowAnswers] = useState(false);

  // Extract modules from exam paper
  const modules: ModuleInfo[] = useMemo(() => {
    if (!examPaper?.sections)
      return [
        { id: "1", title: "Module 1", questionCount: 27 },
        { id: "2", title: "Module 2", questionCount: 27 },
      ];

    return examPaper.sections.map((section, index) => ({
      id: (index + 1).toString(),
      title: `${examPaper.subject || "Unknown"} Module ${index + 1}`,
      questionCount:
        section.questions?.length || section.question_ids?.length || 0,
    }));
  }, [examPaper]);

  // Get current questions for the selected module
  const currentQuestions: IQuestion[] = useMemo(() => {
    if (!examPaper?.sections) {
      // Mock data for demo purposes
      return [
        {
          id: "demo-q1",
          exam_id: id || "demo",
          answer_type: "multiple_choice",
          category_id: "cat1",
          category: "Reading Comprehension",
          passage: `The rise of digital photography has fundamentally transformed how we capture, store, and share memories. Unlike traditional film photography, which required careful consideration of each shot due to limited frames and development costs, digital cameras allow for virtually unlimited experimentation. This shift has democratized photography, making it accessible to millions who might never have picked up a film camera.

However, this accessibility comes with its own challenges. The ease of taking thousands of photos has led to what some experts call "digital hoarding" – the accumulation of vast numbers of images that are rarely organized or revisited. While we have more photos than ever before, the question remains: are we actually preserving our memories more effectively?`,
          question:
            "Which choice best describes the main purpose of the passage?",
          status: "active",
          choices: [
            {
              id: "A",
              text: "To criticize the quality of modern digital photography",
            },
            {
              id: "B",
              text: "To explore both benefits and drawbacks of digital photography",
            },
            {
              id: "C",
              text: "To argue that film photography was superior to digital",
            },
            {
              id: "D",
              text: "To explain the technical differences between film and digital cameras",
            },
          ],
          correct_choice_id: "B",
          created_at: "2024-01-01",
          updated_at: "2024-01-01",
        },
      ];
    }

    const sectionIndex = parseInt(currentModuleId) - 1;
    const section = examPaper.sections[sectionIndex];
    return section?.questions || [];
  }, [examPaper, currentModuleId, id]);

  const currentQuestion = currentQuestions[currentQuestionIdx];

  const handleModuleSelect = (moduleId: string) => {
    setCurrentModuleId(moduleId);
    setCurrentQuestionIdx(0);
  };

  const handleQuestionSelect = (idx: number) => {
    setCurrentQuestionIdx(idx);
  };

  // Local highlighting (not saved to server)
  const handleHighlight = useCallback(
    (contentType: "passage" | "question", content: string) => {
      if (!currentQuestion) return;
      const key = `${currentQuestion.id}_${contentType}`;
      setLocalHighlights((prev) => ({
        ...prev,
        [key]: content,
      }));
    },
    [currentQuestion]
  );

  // Local answer selection (not saved to server)
  const handleAnswerSelect = (choiceId: string) => {
    if (!currentQuestion) return;
    setLocalAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: {
        selected_choice_id: choiceId,
        timestamp: new Date().toISOString(),
      },
    }));
  };

  const getHighlightedContent = (contentType: "passage" | "question") => {
    if (!currentQuestion) return "";
    const key = `${currentQuestion.id}_${contentType}`;
    return (
      localHighlights[key] ||
      (contentType === "passage"
        ? currentQuestion.passage
        : currentQuestion.question)
    );
  };

  const selectedAnswer = currentQuestion
    ? localAnswers[currentQuestion.id]?.selected_choice_id
    : undefined;

  // Loading states
  if (isPaperLoading) {
    return (
      <PageContainer>
        <div className="h-screen flex items-center justify-center">
          <Text>Loading exam paper...</Text>
        </div>
      </PageContainer>
    );
  }

  // Error states
  if (paperError) {
    return (
      <PageContainer>
        <div className="h-screen flex items-center justify-center">
          <div className="text-center">
            <Text className="text-red-600 mb-4">Error loading exam paper</Text>
            <Button onClick={() => window.history.back()}>Go Back</Button>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-[#f7f8fa]">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => window.history.back()}
              plain
              className="rounded-full p-2"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </Button>
            <div>
              <Heading level={2}>Teacher Demo</Heading>
              <Text className="text-sm text-gray-600">
                {examPaper?.title || "Demo Exam"} - Module {currentModuleId} -
                Question {currentQuestionIdx + 1}
              </Text>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Button
              onClick={() => setModuleNavOpen(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              Switch Module
            </Button>
            <Button
              onClick={() => setShowAnswers(!showAnswers)}
              className={`${
                showAnswers
                  ? "bg-green-500 hover:bg-green-600"
                  : "bg-gray-500 hover:bg-gray-600"
              } text-white`}
            >
              {showAnswers ? "Hide Answers" : "Show Answers"}
            </Button>
            <div className="text-sm text-blue-600 font-medium">Demo Mode</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex h-0 min-h-0 overflow-hidden bg-white justify-center">
        {/* Passage Panel */}
        <div className="w-1/2 p-6 border-r border-gray-200 overflow-y-auto">
          <div className="max-w-2xl">
            <Heading level={3} className="mb-4">
              Passage
            </Heading>
            {currentQuestion?.passage ? (
              <div
                className="prose prose-lg leading-relaxed cursor-text border rounded-lg p-4"
                contentEditable
                suppressContentEditableWarning={true}
                onBlur={(e) =>
                  handleHighlight("passage", e.currentTarget.innerHTML)
                }
                dangerouslySetInnerHTML={{
                  __html: getHighlightedContent("passage") || "",
                }}
              />
            ) : (
              <Text className="text-gray-500 italic">
                No passage for this question
              </Text>
            )}
            <Text className="text-xs text-gray-500 mt-2">
              💡 Click and edit text. Use highlighting tools to emphasize key
              points. Changes are saved locally.
            </Text>
          </div>
        </div>

        {/* Question Panel */}
        <div className="w-1/2 p-6 overflow-y-auto">
          <div className="max-w-2xl">
            <Heading level={3} className="mb-4">
              Question {currentQuestionIdx + 1}
            </Heading>

            {currentQuestion ? (
              <>
                <div
                  className="mb-6 text-lg leading-relaxed cursor-text border rounded-lg p-4"
                  contentEditable
                  suppressContentEditableWarning={true}
                  onBlur={(e) =>
                    handleHighlight("question", e.currentTarget.innerHTML)
                  }
                  dangerouslySetInnerHTML={{
                    __html: getHighlightedContent("question") || "",
                  }}
                />

                {/* Answer Choices */}
                {currentQuestion.choices &&
                  currentQuestion.choices.length > 0 && (
                    <div className="space-y-3">
                      {currentQuestion.choices.map((choice) => (
                        <div
                          key={choice.id}
                          className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedAnswer === choice.id
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 hover:border-gray-300"
                          } ${
                            showAnswers &&
                            choice.id === currentQuestion.correct_choice_id
                              ? "ring-2 ring-green-400 bg-green-50"
                              : ""
                          }`}
                          onClick={() => handleAnswerSelect(choice.id)}
                        >
                          <div
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              selectedAnswer === choice.id
                                ? "border-blue-500 bg-blue-500"
                                : "border-gray-300"
                            }`}
                          >
                            <span
                              className={`text-sm font-bold ${
                                selectedAnswer === choice.id
                                  ? "text-white"
                                  : "text-gray-600"
                              }`}
                            >
                              {choice.id}
                            </span>
                          </div>
                          <div className="flex-1">
                            <Text>{choice.text}</Text>
                            {showAnswers &&
                              choice.id ===
                                currentQuestion.correct_choice_id && (
                                <Text className="text-xs text-green-600 font-medium mt-1">
                                  ✓ Correct Answer
                                </Text>
                              )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                {/* Question Info */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <Text className="text-sm font-medium text-blue-800 mb-2">
                    Question Information
                  </Text>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Category:</span>
                      <span className="ml-2 text-gray-600">
                        {currentQuestion.category || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Type:</span>
                      <span className="ml-2 text-gray-600">
                        {currentQuestion.answer_type?.replace("_", " ") ||
                          "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Demo Controls */}
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <Text className="text-sm font-medium text-yellow-800 mb-2">
                    🎯 Demo Controls
                  </Text>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      onClick={() => {
                        setLocalHighlights({});
                        setLocalAnswers({});
                      }}
                      className="text-xs bg-gray-500 hover:bg-gray-600 text-white"
                    >
                      Clear All
                    </Button>
                    <Button
                      onClick={() => {
                        if (currentQuestion) {
                          const passageKey = `${currentQuestion.id}_passage`;
                          const questionKey = `${currentQuestion.id}_question`;
                          setLocalHighlights((prev) => ({
                            ...prev,
                            [passageKey]: currentQuestion.passage || "",
                            [questionKey]: currentQuestion.question || "",
                          }));
                        }
                      }}
                      className="text-xs bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      Reset Text
                    </Button>
                    <Button
                      onClick={() => {
                        if (currentQuestion) {
                          setLocalAnswers((prev) => ({
                            ...prev,
                            [currentQuestion.id]: {
                              selected_choice_id:
                                currentQuestion.correct_choice_id,
                              timestamp: new Date().toISOString(),
                            },
                          }));
                        }
                      }}
                      className="text-xs bg-green-500 hover:bg-green-600 text-white"
                    >
                      Show Correct Answer
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <Text className="text-gray-500">No question available</Text>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="flex justify-between items-center max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-4">
            <Text className="text-sm text-gray-600">
              Question {currentQuestionIdx + 1} of {currentQuestions.length}
            </Text>
            {selectedAnswer && (
              <Text className="text-xs text-blue-600">
                Selected: {selectedAnswer}
              </Text>
            )}
            {showAnswers && currentQuestion && (
              <Text className="text-xs text-green-600">
                Correct: {currentQuestion.correct_choice_id}
              </Text>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={() =>
                handleQuestionSelect(Math.max(0, currentQuestionIdx - 1))
              }
              disabled={currentQuestionIdx === 0}
              className="bg-gray-500 hover:bg-gray-600 text-white disabled:opacity-50"
            >
              Previous
            </Button>
            <Button
              onClick={() =>
                handleQuestionSelect(
                  Math.min(currentQuestions.length - 1, currentQuestionIdx + 1)
                )
              }
              disabled={currentQuestionIdx === currentQuestions.length - 1}
              className="bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Module Navigation Modal */}
      <ModuleNavigationModal
        isOpen={moduleNavOpen}
        onClose={() => setModuleNavOpen(false)}
        modules={modules}
        currentModuleId={currentModuleId}
        onModuleSelect={handleModuleSelect}
      />
    </div>
  );
};

export default TeacherDemoPage;
